import { createSelector } from '@reduxjs/toolkit';
import memoize from 'memoize-one';
import type { RootState } from 'src/root/store';

import type { DenormalizedActivityItem } from '@modules/advancement/advancementHistory/activityUtils';
import type { DenormalizedHistoryItem } from '@modules/advancement/advancementHistory/utils';
import type { RosterScout } from '@modules/advancement/duck/types';
import {
  ProgramId,
  advancementHistoryModuleName,
  advancementModuleName,
  advancementTypes,
  historyItemsTypes,
} from '@modules/shared/constants';
import advancementStatus from '@modules/shared/utils/advancementStatus';
import canApproveAdvancement from '@modules/shared/utils/canApproveAdvancement';
import isEagleScoutRank from '@modules/shared/utils/isEagleScoutRank';
import {
  adventuresSel,
  allAwardsSel,
  meritBadgesSel,
  packRosterItemsSel,
  ranksSel,
  shouldFilterUsersBySubunitsSel,
  unitTypeIdSel,
} from '@shared/duck/selectors/general.selectors';

type Advancement = {
  imageUrl100: string;
  programId: number;
  unitTypeId: number;
  level: number;
  id: number;
};

const advancementHistoryRawItemsSel = (state: RootState) =>
  state[advancementModuleName][advancementHistoryModuleName]
    ?.items as DenormalizedHistoryItem[];

const getAdvancementDictInfo = (
  item: DenormalizedHistoryItem,
  advancements: Array<Advancement[]>,
) => {
  const { advancementType } = item;
  const [awards, ranks, meritBadges, adventures] = advancements;
  const advancementList =
    advancementTypes.AWARDS == advancementType
      ? awards
      : advancementTypes.RANKS == advancementType
      ? ranks
      : advancementTypes.MERIT_BADGES == advancementType
      ? meritBadges
      : adventures;

  const { imageUrl100, programId, unitTypeId, level, id } =
    advancementList.find(({ id }: Advancement) => id == item.id) || {};
  const newProgramId =
    advancementType == advancementTypes.ADVENTURES
      ? ProgramId.CUB_SCOUT
      : programId || unitTypeId;

  return {
    imgUrl: imageUrl100,
    level: level || id,
    programId: newProgramId,
  };
};

const getScoutInfo = memoize(
  (userId: number, packRoster: RosterScout[]) =>
    packRoster.find(rosterItem => rosterItem.userId == userId) || {},
);

function shouldEnableItem(
  record: DenormalizedHistoryItem & { isAdult: boolean },
  packRoster: RosterScout[],
  unitTypeId: number,
) {
  if (record.isAdult && record.type === historyItemsTypes.ADVANCEMENT) {
    return false;
  }
  if (!advancementStatus.isCompleted(record)) {
    return true;
  }
  const {
    currentHighestRankApproved: highestHierarchicalAdvancement,
    otherHighestRanksApproved,
  } = getScoutInfo(record.userId, packRoster);

  const scoutsBSAHighestRank = (otherHighestRanksApproved || []).find(
    ({ programId }: { programId: number }) => programId == ProgramId.BOY_SCOUT,
  );

  return canApproveAdvancement({
    unitTypeId,
    scoutsBSAHighestRank,
    highestHierarchicalAdvancement,
    advancement: record,
    treatEagleScoutAsRegular: true,
  });
}

const advancementHistoryTableItemsSel = createSelector(
  advancementHistoryRawItemsSel,
  packRosterItemsSel,
  unitTypeIdSel,
  shouldFilterUsersBySubunitsSel,
  allAwardsSel,
  ranksSel,
  meritBadgesSel,
  adventuresSel,
  (
    items,
    packRoster,
    unitTypeId,
    shouldFilterUsersBySubunits,
    ...advancements
  ) =>
    items.map(record => {
      const scoutInfo = getScoutInfo(record.userId, packRoster);
      const { isYouth, firstName, lastName, personShortFullName } = scoutInfo;
      const isAdult =
        scoutInfo.isAdult !== undefined
          ? scoutInfo.isAdult
          : !(
              isYouth || (record as unknown as DenormalizedActivityItem).isYouth
            );

      const newRecord = {
        isAdult,
        ...record,
        ...getAdvancementDictInfo(record, advancements),
        firstName: record.firstName || firstName,
        lastName: record.lastName || lastName,
        personShortFullName: personShortFullName,
        name: record.name || personShortFullName,
        ...(shouldFilterUsersBySubunits && {
          positions: scoutInfo.positions,
        }),
      };

      const extraValidationsPass = shouldEnableItem(
        newRecord,
        packRoster,
        unitTypeId,
      );

      const isAdvancementPendingApprovable =
        record.isAdvancementPending && !isEagleScoutRank(newRecord);

      return {
        ...newRecord,
        extraValidationsPass,
        /** Meaning it's pending (status=completed) and can be approved */
        isAdvancementPendingApprovable,
      };
    }),
);

export const advancementHistoryItemsSel = createSelector(
  advancementHistoryTableItemsSel,
  items => items.filter(record => record.extraValidationsPass),
);

/**
 * This is the list for the items we were hiding due to UI business logic that
 * won't allow users to approve certain items.
 */
export const pendingItemsNotApprovableSel = createSelector(
  advancementHistoryTableItemsSel,
  items => items.filter(item => !item.extraValidationsPass),
);
